import React, { useState } from 'react'
import { supabase } from '../supabaseClient'
import { sendWhatsApp } from '../whatsappClient'
import { planClockChange, findTeacherClashes, alreadyAtUsualTime, shiftPlan } from '../clockChange'

// Admin: after a UK/US clock change, move upcoming classes in IST so each
// student abroad keeps the local time they booked. Shows every class first;
// nothing changes until Apply. Calendar invites update by themselves (the
// 15-minute job re-syncs changed classes and Google emails the attendees).

function fmtDate(d) {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
}
function fmtTime(t) {
  return t ? new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'
}
function localTime(date, time, tz) {
  try {
    return new Date(`${date}T${time.slice(0, 5)}:00+05:30`).toLocaleString('en-GB', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: true })
  } catch { return '—' }
}
const ZONE_NAME = { 'Europe/London': 'UK', 'America/New_York': 'New York', 'America/Chicago': 'Chicago', 'America/Los_Angeles': 'California', 'America/Toronto': 'Toronto' }

export default function ClockChange() {
  const [rows,     setRows]     = useState(null)
  const [selected, setSelected] = useState({})
  const [loading,  setLoading]  = useState(false)
  const [applying, setApplying] = useState(false)
  const [msg,      setMsg]      = useState('')
  const [room,     setRoom]     = useState({})

  // Moves the clashing classes of the same student(s) by the same amount,
  // e.g. Kaizad 05:00 -> 06:00 so Dhananjay can go 04:00 -> 05:00.
  function makeRoom(r) {
    const who = names(r.clash.cls.id)
    const extra = {}
    for (const x of rows) if (x.clash && names(x.clash.cls.id) === who) extra[x.clash.cls.id] = x.plan.minutes
    check({ ...room, ...extra })
  }

  // room: { classId: minutes } — other students' classes moved to make room.
  // Only for this preview: Apply's re-check starts without it.
  async function check(room = {}) {
    if (room.nativeEvent) room = {}
    setLoading(true); setMsg('')
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
    const { data: classes, error } = await supabase.from('classes')
      .select('id, title, batch, class_date, start_time, duration_minutes, teacher_id, teacher_name, created_at, is_cancelled')
      .gte('class_date', today).eq('is_cancelled', false).order('class_date').order('start_time')
    if (error) { setMsg(`⛔ ${error.message}`); setLoading(false); return }
    // The last 5 weeks of classes, to spot ones already moved (see alreadyAtUsualTime).
    const since = new Date(Date.now() - 35 * 86400000).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
    const { data: past } = await supabase.from('classes')
      .select('id, teacher_id, class_date, start_time')
      .gte('class_date', since).lt('class_date', today).eq('is_cancelled', false)
    const ids = [...(classes || []), ...(past || [])].map(c => c.id)
    const enr = []
    for (let i = 0; i < ids.length; i += 200) {
      const { data } = await supabase.from('enrollments').select('class_id, student_id, profiles:student_id(full_name, timezone)').in('class_id', ids.slice(i, i + 200))
      enr.push(...(data || []))
    }
    const byClass = {}
    for (const e of enr) if (e.profiles) (byClass[e.class_id] ||= []).push({ ...e.profiles, id: e.student_id })
    const planned = (classes || []).map(cls => {
      const students = byClass[cls.id] || []
      let plan = planClockChange(cls, students.map(s => s.timezone))
      if (plan.action === 'move') {
        const mine = new Set(students.map(s => s.id))
        const earlier = (past || []).filter(p => p.teacher_id === cls.teacher_id && p.start_time && (byClass[p.id] || []).some(s => mine.has(s.id)))
        if (alreadyAtUsualTime(cls, students.find(s => s.timezone).timezone, earlier)) plan = { action: 'skip', reason: 'Already at the usual local time' }
      }
      if (room[cls.id]) plan = { ...shiftPlan(cls, room[cls.id]), room: true }
      return { cls, students, plan }
    })
    setRoom(room)
    const clashes = findTeacherClashes(planned)
    const list = planned.map(r => ({ ...r, clash: clashes.get(r.cls.id) || null }))
    setRows(list)
    setSelected(Object.fromEntries(list.filter(r => r.plan.action === 'move' && !r.clash).map(r => [r.cls.id, true])))
    setLoading(false)
  }

  async function apply() {
    const chosen = rows.filter(r => selected[r.cls.id] && r.plan.action === 'move')
    if (!chosen.length) return
    if (!window.confirm(`Move ${chosen.length} class${chosen.length > 1 ? 'es' : ''} in India time so the students keep their usual local time?\n\nTeachers get one WhatsApp each; calendar invites update by themselves.`)) return
    setApplying(true); setMsg('')
    let moved = 0, failed = 0
    const perTeacher = {}
    for (const r of chosen) {
      const { error } = await supabase.from('classes').update({
        class_date: r.plan.newDate, start_time: r.plan.newTime, reminder_sent_at: null, email_reminder_sent_at: null,
        created_at: new Date().toISOString(), // counts as booked now, so the next check never moves it twice
      }).eq('id', r.cls.id)
      if (error) { failed++; continue }
      moved++
      const d = new Date(r.plan.newDate + 'T00:00:00')
      await supabase.from('events').update({ day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear(), time: r.plan.newTime }).eq('class_id', r.cls.id)
      if (r.cls.teacher_id) (perTeacher[r.cls.teacher_id] ||= []).push(r)
    }
    // One WhatsApp per teacher (class_rescheduled_wa: name, class, from, to).
    const teacherIds = Object.keys(perTeacher)
    const { data: teachers } = teacherIds.length
      ? await supabase.from('profiles').select('id, full_name, phone').in('id', teacherIds)
      : { data: [] }
    for (const t of teachers || []) {
      const list = perTeacher[t.id]
      const mins = list[0].plan.minutes
      if (t.phone) sendWhatsApp('class_rescheduled_wa', t.phone, [
        t.full_name || 'Teacher',
        `${list.length} upcoming class${list.length > 1 ? 'es' : ''} with UK/US students`,
        'their current India times',
        `${Math.abs(mins) / 60} hour ${mins > 0 ? 'later' : 'earlier'} in India time, because the UK/US clocks changed (see your Classes page for each new time)`,
      ], { recipientId: t.id })
    }
    setMsg(`✓ ${moved} class${moved !== 1 ? 'es' : ''} moved${failed ? `, ${failed} failed` : ''}. ${teacherIds.length} teacher${teacherIds.length !== 1 ? 's' : ''} told on WhatsApp.`)
    setApplying(false)
    check()
  }

  const moves = rows ? rows.filter(r => r.plan.action === 'move') : []
  const chosenCount = moves.filter(r => selected[r.cls.id]).length
  const byId = Object.fromEntries((rows || []).map(r => [r.cls.id, r]))
  function names(id) { return (byId[id]?.students || []).map(s => s.full_name).join(', ') }
  const cell = { padding: '9px 10px', borderBottom: '0.5px solid rgba(var(--fg-rgb),0.07)', fontSize: '12px', verticalAlign: 'top' }
  const head = { ...cell, fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(var(--fg-rgb),var(--a35))', textAlign: 'left' }

  return (
    <div style={{ marginTop: '14px', display: 'grid', gap: '14px' }}>
      <div style={{ background: 'rgba(var(--fg-rgb),0.04)', border: '0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius: '14px', padding: '16px', fontSize: '13px', color: 'rgba(var(--fg-rgb),var(--a60))', lineHeight: 1.6 }}>
        Classes are saved in India time. When the UK or US clocks change (UK: last Sunday of March and October; US: second Sunday of March, first Sunday of November), students abroad suddenly have class an hour earlier or later in their own time.
        This moves their upcoming classes in India time so <strong>they keep the local time they booked</strong>. Classes booked after the change, Indian students and mixed groups stay as they are.
        <div style={{ marginTop: '12px' }}>
          <button onClick={check} disabled={loading} style={{ fontSize: '13px', fontWeight: 700, padding: '9px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#1e90ff,#0ea5e9)', color: '#fff', fontFamily: 'inherit', opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Checking…' : rows ? '↻ Check again' : 'Check upcoming classes'}
          </button>
        </div>
      </div>

      {msg && <div style={{ padding: '9px 14px', borderRadius: '9px', fontSize: '13px', background: msg.startsWith('✓') ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: msg.startsWith('✓') ? '#34d399' : '#f87171' }}>{msg}</div>}

      {rows && (
        <div style={{ background: 'rgba(var(--fg-rgb),0.04)', border: '0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius: '14px', padding: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <div style={{ fontSize: '13px', color: 'rgba(var(--fg-rgb),var(--a70))' }}>
              <strong>{moves.length}</strong> class{moves.length !== 1 ? 'es' : ''} need moving{moves.some(r => r.clash) ? ` · ${moves.filter(r => r.clash).length} clash with the teacher's other classes (fix those by hand)` : ''} · {rows.length - moves.length} stay as they are
            </div>
            <button onClick={apply} disabled={applying || !chosenCount} style={{ fontSize: '13px', fontWeight: 700, padding: '9px 18px', borderRadius: '10px', border: 'none', cursor: chosenCount ? 'pointer' : 'not-allowed', background: chosenCount ? 'rgba(16,185,129,0.85)' : 'rgba(100,100,100,0.3)', color: '#fff', fontFamily: 'inherit', opacity: applying ? 0.6 : 1 }}>
              {applying ? 'Moving…' : `Apply to ${chosenCount} class${chosenCount !== 1 ? 'es' : ''}`}
            </button>
          </div>
          {moves.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', fontSize: '13px', color: 'rgba(var(--fg-rgb),var(--a30))' }}>Nothing to move: every upcoming class already keeps its students' local time.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
                <thead><tr><th style={head}></th><th style={head}>Class</th><th style={head}>Teacher</th><th style={head}>India time now → new</th><th style={head}>Student's time (kept)</th></tr></thead>
                <tbody>
                  {moves.map(r => {
                    const tz = r.students.find(s => s.timezone)?.timezone
                    return (
                      <tr key={r.cls.id} style={{ opacity: r.clash ? 0.75 : 1 }}>
                        <td style={cell}><input type="checkbox" id={`cc-${r.cls.id}`} checked={!!selected[r.cls.id]} disabled={!!r.clash} onChange={e => setSelected(s => ({ ...s, [r.cls.id]: e.target.checked }))} /></td>
                        <td style={cell}><label htmlFor={`cc-${r.cls.id}`} style={{ fontWeight: 600, color: 'rgba(var(--fg-rgb),var(--a85))' }}>{r.cls.title}</label><div style={{ color: 'rgba(var(--fg-rgb),var(--a40))' }}>{r.students.map(s => s.full_name).join(', ')}</div></td>
                        <td style={cell}>{r.cls.teacher_name || '—'}</td>
                        <td style={cell}>
                          {fmtDate(r.cls.class_date)} {fmtTime(r.cls.start_time)} → <strong style={{ color: '#34d399' }}>{r.plan.newDate !== r.cls.class_date ? `${fmtDate(r.plan.newDate)} ` : ''}{fmtTime(r.plan.newTime)}</strong>
                          {r.clash && <div style={{ color: '#f87171', marginTop: '3px' }}>
                            ⛔ Clashes with "{r.clash.cls.title}" at {fmtTime(r.clash.time)}{names(r.clash.cls.id) ? ` (${names(r.clash.cls.id)})` : ''}. Keep this class at {fmtTime(r.cls.start_time)}, or{' '}
                            {byId[r.clash.cls.id]?.plan.action === 'move'
                              ? 'move one of them in Reschedule.'
                              : <button onClick={() => makeRoom(r)} disabled={loading} style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '6px', border: '0.5px solid #f87171', background: 'transparent', color: '#f87171', cursor: 'pointer', fontFamily: 'inherit' }}>move {names(r.clash.cls.id) || 'that class'}'s classes {Math.abs(r.plan.minutes) / 60} hour {r.plan.minutes > 0 ? 'later' : 'earlier'} too</button>}
                          </div>}
                          {r.plan.room && <div style={{ color: '#f4a335', marginTop: '3px' }}>Moved to make room. Tell this family their new time.</div>}
                        </td>
                        <td style={cell}>{tz ? `${localTime(r.plan.newDate, r.plan.newTime, tz)} ${ZONE_NAME[tz] || tz}${r.plan.room ? ' (new)' : ''}` : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          {rows.some(r => r.plan.action === 'skip' && /different time zones|not set/.test(r.plan.reason)) && (
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#f4a335', lineHeight: 1.6 }}>
              ⚠ Check by hand: {rows.filter(r => r.plan.action === 'skip' && /different time zones|not set/.test(r.plan.reason)).slice(0, 8).map(r => `${r.cls.title} (${fmtDate(r.cls.class_date)}, ${r.plan.reason.toLowerCase()})`).join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
